import Cookies from "js-cookie";
import React, { type ReactNode } from "react";

import { customFetch } from "@/client/custom-fetch";
import { IAuthenticated,useAuth } from "@/context";

import PermissionsContext, { type TPermission } from "./permissions-context";

export const PermissionsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { auth } = useAuth();
  const [permissions, setPermissions] = React.useState<TPermission[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);

  // Buscar permissões do usuário quando autenticado
  React.useEffect(() => {
    const fetchUserPermissions = async () => {
      // Verificar se há token no cookie (para casos de reload da página)
      let authUser: IAuthenticated | null = null;
      try {
        const authUserCookie = Cookies.get("authUser");
        if (authUserCookie) {
          authUser = JSON.parse(authUserCookie);
        }
      } catch (error) {
        console.error("Erro ao ler cookie authUser:", error);
      }

      // Usar auth do contexto ou do cookie (para casos de reload)
      const isAuthenticated = auth.isAuthenticated || (authUser?.token ? true : false);
      const userId = auth.id || authUser?.id;
      const token = auth.token || authUser?.token;

      if (!isAuthenticated || !userId || !token) {
        setPermissions([]);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const userPermissions = await customFetch<{ permissions: TPermission[] }>(
          `/users/permissions/${userId}`,
          {
            method: "GET",
          }
        );
        setPermissions(userPermissions?.permissions || []);
      } catch (error) {
        console.error("Erro ao buscar permissões do usuário:", error);
        setPermissions([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserPermissions();
  }, [auth.isAuthenticated, auth.id, auth.token]);

  // Verificar se o usuário tem uma permissão específica
  const hasPermission = React.useCallback(
    (permissionCode: string): boolean => {
      if (!permissionCode || permissions.length === 0) return false;
      return permissions.some((perm) => perm.code === permissionCode);
    },
    [permissions]
  );

  // Verificar se o usuário tem pelo menos uma das permissões
  const hasAnyPermission = React.useCallback(
    (permissionCodes: string[]): boolean => {
      if (!permissionCodes.length || permissions.length === 0) return false;
      return permissionCodes.some((code) => hasPermission(code));
    },
    [permissions, hasPermission]
  );

  // Verificar se o usuário tem todas as permissões
  const hasAllPermissions = React.useCallback(
    (permissionCodes: string[]): boolean => {
      if (!permissionCodes.length || permissions.length === 0) return false;
      return permissionCodes.every((code) => hasPermission(code));
    },
    [permissions, hasPermission]
  );

  return (
    <PermissionsContext.Provider
      value={{
        permissions,
        setPermissions,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        isLoading,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};

